import {
  Close as CloseIcon,
  FileDownloadOutlined as DownloadIcon,
  Language as WebIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from "@mui/material";
import { dpString, parseNumberString } from "common/numbers";
import DefaultLoader from "components/common/DefaultLoader";
import SourcedValue from "components/common/SourcedValue";
import {
  FIELD_LABELS,
  REVIEW_SECTIONS,
} from "components/common/submissionReviewFields";
import { useEffect, useMemo, useState } from "react";
import {
  normaliseSourcing,
  resolveSourcedValue,
  sourceRecordFor,
  SOURCE_SUBMISSION,
  SOURCE_WEB,
} from "utils/fieldSourcing";

// ─── Design tokens (from the "AI Review Page" Claude Design, layout 1c) ─────────
const GREEN = "#2CAB63";
const DEEP = "#006B36";
const MAG = "#CC1B7F";
const INK = "#262626";
const MUT = (a) => `rgba(38,38,38,${a})`;

// Confidence at or above this reads as high-confidence (green chip); below it,
// low-confidence (magenta). Confidence arrives as a 0–1 float. This only colours
// the chip — nothing is marked verified until the reviewer approves it.
const HIGH_CONFIDENCE_THRESHOLD = 0.9;

// ─── Value + source formatting ──────────────────────────────────────────────────

const formatNumber = dpString(2, { minDp: 0 });

const formatValue = (val) => {
  if (val == null) return null;
  if (typeof val === "object" && !Array.isArray(val)) {
    const { code, title } = val;
    if (code != null && title) return `${code} – ${title}`;
    if (code != null) return String(code);
    return null;
  }
  // Thousand separators — a raw 150000000 is unreadable. parseNumberString decides what
  // counts as a number, and coerceEdit parses the edit back with the same helper.
  if (typeof val === "number") return formatNumber(val);
  const s = String(val).trim();
  if (s === "") return null;
  const numeric = parseNumberString(s);
  return numeric == null ? s : formatNumber(numeric);
};

// Reviewers edit the display string, so an edit has to be turned back into the shape
// the AI value had — a bare string would render as "(undefined) undefined" for NAICS.
const EDIT_INVALID = Symbol("editInvalid");

const coerceEdit = (raw, text) => {
  const trimmed = String(text).trim();
  if (trimmed === "") return EDIT_INVALID;
  if (typeof raw === "number") {
    const n = parseNumberString(trimmed);
    return n == null ? EDIT_INVALID : n;
  }
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const match = trimmed.match(/^(\d+)\s*[–-]\s*(.+)$/);
    if (match) return { code: Number(match[1]), title: match[2].trim() };
    if (raw.title == null && /^\d+$/.test(trimmed))
      return { code: Number(trimmed) };
    return EDIT_INVALID;
  }
  return trimmed;
};

const locationLabel = (loc) =>
  `${loc.filename}${loc.page_number ? ` · p.${loc.page_number}` : ""}`;

const formatSource = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return null;
  const first = locations[0];
  if (!first?.filename) return null;
  const more = locations.length > 1 ? ` +${locations.length - 1}` : "";
  return { text: locationLabel(first), more };
};

// Every source, one per line — used for the row's hover tooltip.
const formatSourceFull = (locations) => {
  if (!Array.isArray(locations)) return "";
  return locations
    .filter((l) => l?.filename)
    .map(locationLabel)
    .join("\n");
};

// ─── Square checkbox (matches the design's rounded accent-green box) ─────────────

const CheckBox = ({ checked, indeterminate, onClick, disabled, label }) => (
  <Box
    component={"button"}
    type={"button"}
    onClick={onClick}
    disabled={disabled}
    aria-checked={indeterminate ? "mixed" : checked}
    aria-label={label}
    role={"checkbox"}
    sx={{
      width: 18,
      height: 18,
      flex: "none",
      borderRadius: "5px",
      border: `1.5px solid ${checked || indeterminate ? GREEN : MUT(0.28)}`,
      bgcolor: checked || indeterminate ? GREEN : "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      p: 0,
      transition: "all .14s",
    }}
  >
    {checked && (
      <Box
        sx={{
          width: 5,
          height: 9,
          mb: "1.5px",
          border: "solid #fff",
          borderWidth: "0 2px 2px 0",
          transform: "rotate(45deg)",
        }}
      />
    )}
    {!checked && indeterminate && (
      <Box sx={{ width: 8, height: "2px", bgcolor: "#fff", borderRadius: 1 }} />
    )}
  </Box>
);

// ─── Pill button (matches .rvw-btn variants) ────────────────────────────────────

const VARIANTS = {
  primary: { bgcolor: GREEN, color: "#fff", "&:hover": { bgcolor: DEEP } },
  soft: {
    bgcolor: "rgba(44,171,99,.12)",
    color: DEEP,
    "&:hover": { bgcolor: "rgba(44,171,99,.2)" },
  },
  outline: {
    bgcolor: "#fff",
    color: MUT(0.6),
    border: `1px solid ${MUT(0.18)}`,
    "&:hover": { borderColor: MUT(0.4) },
  },
  section: {
    bgcolor: "transparent",
    color: DEEP,
    border: "1px solid rgba(44,171,99,.4)",
    "&:hover": { bgcolor: "rgba(44,171,99,.08)" },
  },
  verified: {
    bgcolor: "rgba(44,171,99,.14)",
    color: DEEP,
    "&:hover": { bgcolor: "rgba(44,171,99,.22)" },
  },
  rejected: {
    bgcolor: "rgba(204,27,127,.10)",
    color: MAG,
    "&:hover": { bgcolor: "rgba(204,27,127,.18)" },
  },
};

const Pill = ({ variant = "primary", onClick, disabled, sx, children }) => (
  <Box
    component={"button"}
    type={"button"}
    onClick={onClick}
    disabled={disabled}
    sx={{
      fontSize: 12.5,
      fontWeight: 600,
      lineHeight: 1,
      borderRadius: "9999px",
      cursor: disabled ? "default" : "pointer",
      border: "none",
      whiteSpace: "nowrap",
      px: "15px",
      py: "9px",
      opacity: disabled ? 0.5 : 1,
      transition: "all .16s cubic-bezier(.22,1,.36,1)",
      "&:active": { transform: disabled ? "none" : "scale(.97)" },
      ...VARIANTS[variant],
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ─── Confidence chip ─────────────────────────────────────────────────────────────

const ConfidenceChip = ({ confidence, missing }) => {
  let text;
  let color;
  let bg;
  let dot;
  if (missing) {
    text = "Not found";
    color = MUT(0.5);
    bg = MUT(0.06);
    dot = MUT(0.4);
  } else if (typeof confidence !== "number") {
    text = "No score";
    color = MUT(0.5);
    bg = MUT(0.06);
    dot = MUT(0.4);
  } else {
    const pct = Math.round(confidence * 100);
    const high = confidence >= HIGH_CONFIDENCE_THRESHOLD;
    text = `${pct}%`;
    color = high ? DEEP : MAG;
    bg = high ? "rgba(44,171,99,.12)" : "rgba(204,27,127,.10)";
    dot = high ? GREEN : MAG;
  }
  return (
    <Box
      component={"span"}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontSize: 11.5,
        fontWeight: 700,
        color,
        bgcolor: bg,
        px: "9px",
        py: "3px",
        borderRadius: "9999px",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component={"span"}
        sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: dot }}
      />
      {text}
    </Box>
  );
};

// ─── Agent search chip ──────────────────────────────────────────────────────────

// Each chip opens its tooltip with what it means — the labels are short enough to be
// read as jargon otherwise.
const WEB_CHIP_STYLES = {
  match: {
    color: DEEP,
    bg: "rgba(44,171,99,.12)",
    label: "Web ✓",
    meaning: "The web agrees with the documents.",
  },
  mismatch: {
    color: MAG,
    bg: "rgba(204,27,127,.10)",
    label: "Web ✗",
    meaning: "The web found something different from the value in this field.",
  },
  // web_only: nothing was compared, so this is a value the web supplied rather than an
  // agreement or a disagreement.
  sourced: {
    color: DEEP,
    bg: "rgba(44,171,99,.12)",
    label: "Web filled",
    meaning: "The documents had no value for this field — the web supplied it.",
  },
  not_found: {
    color: MUT(0.5),
    bg: MUT(0.06),
    label: "Web —",
    meaning: "The web found nothing for this field.",
  },
  uncertain: {
    color: MUT(0.5),
    bg: MUT(0.06),
    label: "Web ?",
    meaning: "The web was not conclusive on this field.",
  },
};

/**
 * The version that was not used, shown under the one that was.
 *
 * The submission stores one value per field, so applying either source discards the other
 * from the form. Both are kept on the record, and the loser is shown here so the reviewer
 * can see what was passed over and take it instead in one click.
 */
/**
 * Whether the passed-over value can be applied in a click, and how.
 *
 * Going back to the extracted value is always safe. Going the other way is not: the web
 * answers in prose, and only the enum-resolved form of it is storable.
 */
const alternateApply = (record, check, key, editable, onDraft) => {
  if (!editable) return null;
  if (record.preferred === SOURCE_WEB) {
    return record.submission == null
      ? null
      : () => onDraft(key, String(record.submission));
  }
  return check?.apply_exact && check.apply_value
    ? () => onDraft(key, String(check.apply_value))
    : null;
};

const AlternateSourceLine = ({ record, onUse }) => {
  const preferredWeb = record.preferred === SOURCE_WEB;
  const altValue = preferredWeb ? record.submission : record.web;
  // Nothing to show when the other source was silent, or said the same thing.
  if (
    altValue == null ||
    altValue === "" ||
    altValue === record[record.preferred]
  ) {
    return null;
  }
  // A web value that is prose rather than a selectable option is shown but not
  // clickable — the chip beside it is what offers the closest match.
  const usable = Boolean(onUse);

  return (
    <Tooltip
      arrow
      enterDelay={400}
      title={`${
        preferredWeb ? "Extracted from the documents" : "Found on the web"
      }: ${altValue}${usable ? "\n\nClick to use this instead." : ""}`}
    >
      <Box
        component={usable ? "button" : "div"}
        type={usable ? "button" : undefined}
        onClick={
          usable
            ? (e) => {
                e.stopPropagation();
                onUse();
              }
            : undefined
        }
        sx={{
          display: "flex",
          maxWidth: "100%",
          minWidth: 0,
          // Lines up with the text inside the InputBase above, which carries px: 4px.
          pl: "4px",
          mt: "1px",
          border: "none",
          background: "none",
          p: 0,
          pt: "1px",
          textAlign: "left",
          ...(usable && { cursor: "pointer" }),
        }}
      >
        <SourcedValue
          source={preferredWeb ? SOURCE_SUBMISSION : SOURCE_WEB}
          value={String(altValue)}
          governed={false}
        />
      </Box>
    </Tooltip>
  );
};

const WebCheckChip = ({ check, record, onApplyWebValue }) => {
  // "From web" has to mean the web's answer is the one in the field. When the field kept
  // its own value — the enum guard blocked the web's, or the rule prefers the documents —
  // the web is offering an alternative, which reads as a disagreement whatever the agent
  // called the verdict.
  const webPassedOver = Boolean(
    check.web_value &&
      record &&
      record.preferred !== SOURCE_WEB &&
      check.web_value !== record.submission
  );
  const style =
    WEB_CHIP_STYLES[webPassedOver ? "mismatch" : check.verdict] ||
    WEB_CHIP_STYLES.uncertain;
  // apply_value is set only when the value is actually storable — for enum-backed
  // fields that means it resolved to a real option in the selector's list. "sourced" is
  // web_only's success case and is just as applicable as a verify mismatch.
  const actionableVerdict =
    check.verdict === "mismatch" || check.verdict === "sourced";
  const applicable = Boolean(
    onApplyWebValue && actionableVerdict && check.apply_value
  );
  const tooltip = [
    style.meaning,
    check.reasoning,
    check.web_value ? `Web value: ${check.web_value}` : null,
    check.enum_note,
    applicable
      ? `Click to use ${
          check.apply_value === check.web_value
            ? "the web value"
            : `"${check.apply_value}"`
        }.`
      : null,
    ...(check.source_urls || []).slice(0, 3),
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip arrow title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltip}</Box>}>
      <Box
        component={"span"}
        onClick={
          applicable
            ? (e) => {
                e.stopPropagation();
                onApplyWebValue(check.apply_value);
              }
            : undefined
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexShrink: 0,
          fontSize: 10.5,
          fontWeight: 700,
          color: style.color,
          bgcolor: style.bg,
          px: "7px",
          py: "2px",
          borderRadius: "9999px",
          whiteSpace: "nowrap",
          cursor: applicable ? "pointer" : "default",
          ...(applicable && {
            "&:hover": { bgcolor: "rgba(204,27,127,.18)" },
          }),
        }}
      >
        {style.label}
      </Box>
    </Tooltip>
  );
};

// ─── Column layout ───────────────────────────────────────────────────────────────

const COLS = {
  check: { width: 18, flex: "none" },
  field: { width: 210, flex: "none" },
  value: { flex: 1, minWidth: 0 },
  web: { width: 22, flex: "none" },
  source: { width: 210, flex: "none" },
  conf: { width: 96, flex: "none" },
  status: { width: 172, flex: "none" },
};

// `webSourcedKeys` are kept even when the documents produced nothing: a rule whose sources
// include the web exists precisely to fill a gap the documents left, and hiding the row made
// that configuration a no-op for its main use case.
// Rows a rule already web-sources start marked for research.
const initVerifyPicks = (grouped, modes) => {
  const picks = {};
  grouped.forEach((g) =>
    g.fields.forEach((f) => {
      const mode = (modes || {})[f.key];
      if (mode && mode !== "off") picks[f.key] = true;
    })
  );
  return picks;
};

const buildFields = (ingestionFields, sections, labels, webSourcedKeys) => {
  const ing = ingestionFields || {};
  const keepEmpty = webSourcedKeys || new Set();
  return sections.map((sec) => {
    const fields = sec.keys
      .map((key) => {
        const entry = ing[key];
        const value = entry ? formatValue(entry.value) : null;
        // Fields the AI didn't extract are hidden, unless the web may supply one.
        if (value == null && !keepEmpty.has(key)) return null;
        return {
          key,
          section: sec.name,
          label: labels[key] ?? key,
          value,
          raw: entry?.value ?? null,
          confidence:
            typeof entry?.confidence === "number" ? entry.confidence : null,
          source: formatSource(entry?.source_locations),
          sourceFull: formatSourceFull(entry?.source_locations),
          sourceLoc: entry?.source_locations?.[0] || null,
          reasoning: entry?.reasoning || null,
        };
      })
      .filter(Boolean);
    return { name: sec.name, fields };
  });
};

// Nothing is auto-verified or pre-selected — every field starts pending and the
// reviewer explicitly approves/rejects (or bulk-selects) what to carry over.
const initStatus = (grouped) => {
  const out = {};
  grouped.forEach((g) => g.fields.forEach((f) => (out[f.key] = "pending")));
  return out;
};

// A field is marked for research on its own account: carrying a value over and checking
// it against the web are separate decisions, and most carried fields want no check.
const VerifyToggle = ({ marked, label, onClick }) => (
  <Tooltip
    arrow
    enterDelay={300}
    title={marked ? "Checking this against the web" : "Check against the web"}
  >
    <Box
      component={"button"}
      type={"button"}
      onClick={onClick}
      aria-pressed={Boolean(marked)}
      aria-label={`Check ${label} against the web`}
      sx={{
        ...COLS.web,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        background: "none",
        p: 0,
        cursor: "pointer",
        color: marked ? GREEN : MUT(0.22),
        transition: "color .14s",
        "&:hover": { color: marked ? DEEP : MUT(0.5) },
      }}
    >
      <WebIcon sx={{ fontSize: "1rem" }} />
    </Box>
  </Tooltip>
);

// ─── Row ─────────────────────────────────────────────────────────────────────────

const FieldRow = ({
  field,
  status,
  selected,
  onToggle,
  showVerify = false,
  verifyMarked = false,
  onToggleVerify,
  onApprove,
  onReject,
  onReset,
  onDownloadSource,
  showConfidence,
  editable = false,
  draft,
  invalid = false,
  onDraft,
  onRevert,
  webCheck = null,
  sourceRecord = null,
}) => {
  const { key, label, value, source, sourceFull, sourceLoc, reasoning } = field;
  const isOverridden = draft != null;
  const isApproved = status === "approved";
  const isRejected = status === "rejected";
  const isPending = status === "pending";
  // The caller resolves the file (source citations only carry a filename), so a
  // filename + handler is enough to offer the download.
  const downloadable = Boolean(onDownloadSource && sourceLoc?.filename);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        px: "26px",
        py: "11px",
        borderBottom: `1px solid ${MUT(0.07)}`,
        bgcolor: isApproved ? "rgba(44,171,99,.045)" : "#fff",
        opacity: isRejected ? 0.6 : 1,
        transition: "all .16s",
      }}
    >
      <CheckBox
        checked={!!selected}
        onClick={() => onToggle(key)}
        label={`Select ${label}`}
      />

      {showVerify && (
        <VerifyToggle
          marked={verifyMarked}
          label={label}
          onClick={() => onToggleVerify(key)}
        />
      )}

      <Typography
        sx={{ ...COLS.field, fontSize: 12.5, fontWeight: 600, color: MUT(0.6) }}
        noWrap
      >
        {label}
      </Typography>

      <Box sx={{ ...COLS.value, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: editable ? "4px" : "6px",
            minWidth: 0,
          }}
        >
          {/* On the value alone — the chip and the alternate line carry their own
              tooltips, and a wrapper around all three stacked them on one hover. */}
          <Tooltip
            arrow
            enterDelay={400}
            title={
              <Box sx={{ whiteSpace: "pre-line" }}>
                {(() => {
                  const head = isOverridden ? `AI: ${value}` : value;
                  return reasoning ? `${head}\n\n${reasoning}` : head;
                })()}
              </Box>
            }
          >
            {editable ? (
              <InputBase
                value={draft ?? value ?? ""}
                onChange={(e) => onDraft(key, e.target.value)}
                fullWidth
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: INK,
                  px: "4px",
                  borderRadius: "3px 3px 0 0",
                  bgcolor: isOverridden
                    ? "rgba(204,27,127,0.04)"
                    : "transparent",
                  borderBottom: `1px solid ${
                    invalid
                      ? MAG
                      : isOverridden
                      ? "rgba(204,27,127,0.55)"
                      : "transparent"
                  }`,
                  "&:hover": {
                    borderBottomColor: invalid ? MAG : MUT(0.25),
                  },
                  "& .MuiInputBase-input": { p: 0 },
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: INK,
                  minWidth: 0,
                }}
                noWrap
              >
                {value}
              </Typography>
            )}
          </Tooltip>
          {webCheck && (
            <WebCheckChip
              check={webCheck}
              record={sourceRecord}
              onApplyWebValue={
                editable ? (webValue) => onDraft(key, String(webValue)) : null
              }
            />
          )}
          {editable && isOverridden && (
            <IconButton
              size={"small"}
              onClick={() => onRevert(key)}
              sx={{ p: "2px", color: MAG }}
            >
              <UndoIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          )}
        </Box>
        {sourceRecord && (
          <AlternateSourceLine
            record={sourceRecord}
            onUse={alternateApply(
              sourceRecord,
              webCheck,
              key,
              editable,
              onDraft
            )}
          />
        )}
      </Box>

      <Tooltip
        arrow
        enterDelay={400}
        title={
          sourceFull ? (
            <Box sx={{ whiteSpace: "pre-line" }}>
              {downloadable ? `${sourceFull}\n\nClick to download` : sourceFull}
            </Box>
          ) : (
            ""
          )
        }
      >
        <Box
          component={downloadable ? "button" : "div"}
          type={downloadable ? "button" : undefined}
          onClick={downloadable ? () => onDownloadSource(sourceLoc) : undefined}
          sx={{
            ...COLS.source,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 11,
            color: MUT(0.52),
            textAlign: "left",
            border: "none",
            background: "none",
            p: 0,
            ...(downloadable && {
              cursor: "pointer",
              "&:hover": { color: DEEP },
              "&:hover .rvw-dl": { opacity: 1 },
            }),
          }}
        >
          {downloadable && (
            <DownloadIcon
              className={"rvw-dl"}
              sx={{
                fontSize: "0.9rem",
                flex: "none",
                opacity: 0.55,
                transition: "opacity .14s",
              }}
            />
          )}
          <Box
            component={"span"}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {source ? source.text : "—"}
          </Box>
          {source?.more && (
            <Box component={"span"} sx={{ flex: "none", color: MUT(0.4) }}>
              {source.more}
            </Box>
          )}
        </Box>
      </Tooltip>

      {showConfidence && (
        <Box sx={COLS.conf}>
          <ConfidenceChip confidence={field.confidence} />
        </Box>
      )}

      <Box
        sx={{
          ...COLS.status,
          display: "flex",
          justifyContent: "flex-end",
          gap: "6px",
        }}
      >
        {isPending ? (
          <>
            <Pill
              variant={"outline"}
              sx={{ px: "11px", py: "5px", fontSize: 11.5 }}
              onClick={() => onReject(key)}
            >
              {"Reject"}
            </Pill>
            <Pill
              variant={"primary"}
              sx={{ px: "13px", py: "5px", fontSize: 11.5 }}
              onClick={() => onApprove(key)}
            >
              {"Approve"}
            </Pill>
          </>
        ) : isApproved ? (
          <Pill
            variant={"verified"}
            sx={{ px: "12px", py: "5px", fontSize: 11.5 }}
            onClick={() => onReset(key)}
          >
            {"✓ Verified"}
          </Pill>
        ) : (
          <Pill
            variant={"rejected"}
            sx={{ px: "12px", py: "5px", fontSize: 11.5 }}
            onClick={() => onReset(key)}
          >
            {"Rejected"}
          </Pill>
        )}
      </Box>
    </Box>
  );
};

// ─── Dialog ──────────────────────────────────────────────────────────────────────

const SubmissionReviewDialog = ({
  open,
  title,
  ingestionFields,
  sections = REVIEW_SECTIONS,
  labels = FIELD_LABELS,
  confirmLabel = "Create submission →",
  onClose,
  onConfirm,
  onDownloadSource,
  showConfidence = false,
  editable = false,
  // Agent search: onAgentSearch(insuredName, {key: value}, {key: mode}) → {results, company_summary}.
  // agentSearchConfig: {enabled, modes: {key: mode}, sourcing: {key: FieldSourcing}} —
  // per-rule sourcing; the reviewer picks which fields to verify.
  onAgentSearch = null,
  agentSearchConfig = null,
  // Cross-checks an enum-backed value against its selector's option list:
  // resolveEnum(fieldKey, value) → {status, value, label, candidates}. Without it,
  // web values are taken at face value.
  resolveEnum = null,
}) => {
  // Fields whose rule allows a web value must be shown even if unextracted, so the agent
  // has somewhere to put what it finds.
  const webSourcedKeys = useMemo(() => {
    const modes = agentSearchConfig?.modes || {};
    return new Set(
      Object.keys(modes).filter((k) => modes[k] && modes[k] !== "off")
    );
  }, [agentSearchConfig]);

  const grouped = useMemo(
    () =>
      buildFields(ingestionFields, sections, labels, webSourcedKeys).filter(
        (g) => g.fields.length > 0
      ),
    [ingestionFields, sections, labels, webSourcedKeys]
  );

  const [status, setStatus] = useState({});
  const [selected, setSelected] = useState({});
  // Which rows get researched. Separate from `selected`, which is about carrying a value
  // into the submission.
  const [verifyPicks, setVerifyPicks] = useState({});
  // Edits are held as the display text the reviewer typed; coerced back to the AI
  // value's shape only at confirm. Absent key == not overridden.
  const [drafts, setDrafts] = useState({});
  // Agent search results, keyed by field. null until a search has run.
  const [webChecks, setWebChecks] = useState(null);
  // Both versions of each searched field, keyed by ingestion key. Kept whichever one won
  // so the discarded one stays visible and gets persisted with the submission.
  const [sourceRecords, setSourceRecords] = useState({});
  const [webLoading, setWebLoading] = useState(false);
  // The run's company summary. Kept so it can be filed with the field sources.
  const [webSummary, setWebSummary] = useState(null);
  // The endpoint answers HTTP 200 with {success: false} on failure, so without this a
  // failed search renders exactly like "the web found nothing".
  const [searchError, setSearchError] = useState(null);

  useEffect(() => {
    if (open) {
      setStatus(initStatus(grouped));
      setSelected({});
      // A rule that web-sources a field already said it wants researching, so those rows
      // start marked. Nothing runs until the reviewer says so.
      setVerifyPicks(initVerifyPicks(grouped, agentSearchConfig?.modes));
      setDrafts({});
      setWebChecks(null);
      // Cleared with the checks: the dialog instance survives across items, and stale
      // records would render — and be persisted — as the NEXT submission's provenance.
      setSourceRecords({});
      setWebLoading(false);
      setSearchError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grouped]);

  const allFields = useMemo(() => grouped.flatMap((g) => g.fields), [grouped]);

  // An edited field is approved implicitly — otherwise the edit is silently discarded.
  const setDraft = (key, text) => {
    setDrafts((d) => ({ ...d, [key]: text }));
    setStatus((s) => ({ ...s, [key]: "approved" }));
  };

  const revertDraft = (key) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });

  // Coerce every draft once, so the confirm pill can block on unparseable text.
  const coerced = useMemo(() => {
    const out = {};
    allFields.forEach((f) => {
      if (drafts[f.key] == null) return;
      out[f.key] = coerceEdit(f.raw, drafts[f.key]);
    });
    return out;
  }, [allFields, drafts]);

  const invalidKeys = useMemo(
    () => Object.keys(coerced).filter((k) => coerced[k] === EDIT_INVALID),
    [coerced]
  );

  // ── Agent search ────────────────────────────────────────────────────────────
  // The reviewer marks the rows to check against the web. A rule that web-sources a
  // field sets its mode; every other field is verified against what was extracted.
  const searchModes = useMemo(() => {
    const ruleModes = agentSearchConfig?.modes || {};
    const out = {};
    allFields.forEach((f) => {
      if (!verifyPicks[f.key]) return;
      const mode = ruleModes[f.key];
      out[f.key] = mode && mode !== "off" ? mode : "verify";
    });
    return out;
  }, [agentSearchConfig, allFields, verifyPicks]);

  const searchFieldKeys = useMemo(() => Object.keys(searchModes), [
    searchModes,
  ]);

  const showVerify = Boolean(onAgentSearch && agentSearchConfig?.enabled);

  const runAgentSearch = async () => {
    if (!onAgentSearch || webLoading || searchFieldKeys.length === 0) return;
    setSearchError(null);
    // Never fall back to the filename: researching "Q3_submission_final_v2.pdf" as a
    // company anchors every answer on the wrong entity. Same refusal as triage time.
    const insuredName = ingestionFields?.insured?.value;
    if (!insuredName) {
      setSearchError("No insured name extracted — web search skipped");
      return;
    }
    setWebLoading(true);
    try {
      const fields = {};
      allFields.forEach((f) => {
        if (searchFieldKeys.includes(f.key)) fields[f.key] = f.value;
      });
      const result = await onAgentSearch(
        String(insuredName),
        fields,
        searchModes
      );
      if (result?.success === false) {
        // Leave webChecks null rather than {} — an empty object reads as "searched, found
        // nothing", which is a different and misleading claim.
        setSearchError(result.error || "Agent search failed");
        return;
      }
      if (result?.company_summary) setWebSummary(result.company_summary);
      const byKey = {};
      (result?.results || []).forEach((v) => {
        // apply_value is what clicking the chip writes; apply_exact says whether
        // it is certain enough to write without the reviewer asking.
        byKey[v.field] = {
          ...v,
          apply_value: v.web_value || null,
          apply_exact: true,
        };
      });

      // A web value is prose, so for enum-backed fields it is often not a
      // selectable option ("Rail Transportation" when the list holds "Freight
      // Rail"). Resolve before offering it, so the reviewer never applies a value
      // the submission form cannot store.
      if (resolveEnum) {
        await Promise.all(
          Object.values(byKey).map(async (v) => {
            const offersValue =
              v.verdict === "mismatch" || v.verdict === "sourced";
            if (!offersValue || !v.web_value) return;
            const res = await resolveEnum(v.field, v.web_value);
            if (
              !res ||
              res.status === "resolved" ||
              res.status === "unchecked"
            ) {
              if (res?.label) v.apply_value = res.label;
              return;
            }
            if (res.status === "ambiguous" && res.candidates?.length) {
              // Offer the closest option on click, but never pick it automatically —
              // silently swapping in a guessed classification is worse than leaving
              // the extracted value for the reviewer to judge.
              v.apply_value = res.candidates[0].label;
              v.apply_exact = false;
              v.enum_note = `Not an exact option — closest match is "${res.candidates[0].label}".`;
              return;
            }
            v.apply_value = null;
            v.apply_exact = false;
            v.enum_note = "No matching option in this field's list.";
          })
        );
      }
      setWebChecks((prev) => ({ ...(prev || {}), ...byKey }));
      // One policy decides every application: both sources answered → the rule's
      // preference wins; only one answered → that one; neither → leave it alone. Applied
      // values become normal draft overrides — auto-approved, revertable via the row's
      // undo, and tracked as changed fields like any manual edit.
      const byFieldKey = new Map(allFields.map((f) => [f.key, f]));
      const records = {};
      Object.values(byKey).forEach((v) => {
        // The reviewer asked for the web on this row, so it is a source here whatever the
        // rule said; the rule's preference still decides which value stands.
        const rule = normaliseSourcing(agentSearchConfig?.sourcing?.[v.field]);
        const resolution = resolveSourcedValue({
          submissionValue: byFieldKey.get(v.field)?.value,
          check: v,
          sourcing: {
            ...rule,
            sources: [...new Set([...rule.sources, SOURCE_WEB])],
          },
        });
        // apply_exact is the enum guard: never auto-write a value that only approximately
        // matches the selector's option list.
        const takeWeb =
          editable &&
          v.apply_exact &&
          resolution.source === SOURCE_WEB &&
          resolution.value != null;
        if (takeWeb) {
          const shown = byFieldKey.get(v.field)?.value;
          if (String(resolution.value) === String(shown ?? "")) {
            // The web confirmed what the documents said. Nothing to rewrite, but the row
            // is settled, so it still counts as reviewed.
            setStatus((st) => ({ ...st, [v.field]: "approved" }));
          } else {
            setDraft(v.field, String(resolution.value));
          }
        }
        // A web value the guard blocked leaves the submission's own value standing, so
        // that is what the record has to say was used.
        const applied = takeWeb
          ? resolution
          : {
              ...resolution,
              value: resolution.submissionValue,
              source:
                resolution.submissionValue == null ? null : SOURCE_SUBMISSION,
            };
        const record = sourceRecordFor(applied, v);
        if (record) {
          records[v.field] = record;
        }
      });
      setSourceRecords((prev) => ({ ...prev, ...records }));
    } catch (e) {
      setSearchError(e?.message || "Agent search failed");
    } finally {
      setWebLoading(false);
    }
  };

  // Only verify-mode disagreements are "findings" worth a banner — a web_only field has
  // nothing to disagree with.
  const webMismatchCount = useMemo(
    () =>
      Object.values(webChecks || {}).filter(
        (v) => v.verdict === "mismatch" && v.mode !== "web_only"
      ).length,
    [webChecks]
  );

  const toggleVerify = (key) =>
    setVerifyPicks((s) => {
      const next = { ...s };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });

  const toggle = (key) =>
    setSelected((s) => {
      const next = { ...s };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });

  const setFieldStatus = (key, value) =>
    setStatus((s) => ({ ...s, [key]: value }));

  const approve = (key) => setFieldStatus(key, "approved");
  const reject = (key) => setFieldStatus(key, "rejected");
  const reset = (key) => setFieldStatus(key, "pending");

  const approveSelected = () =>
    setStatus((s) => {
      const next = { ...s };
      Object.keys(selected).forEach((k) => {
        next[k] = "approved";
      });
      return next;
    });

  const approveSection = (name) =>
    setStatus((s) => {
      const next = { ...s };
      allFields.forEach((f) => {
        if (f.section === name) next[f.key] = "approved";
      });
      return next;
    });

  const toggleSection = (name) =>
    setSelected((sel) => {
      const keys = allFields
        .filter((f) => f.section === name)
        .map((f) => f.key);
      const all = keys.length > 0 && keys.every((k) => sel[k]);
      const next = { ...sel };
      keys.forEach((k) => {
        if (all) delete next[k];
        else next[k] = true;
      });
      return next;
    });

  // Only fields the reviewer explicitly approved are carried over. Pending
  // (untouched) and rejected fields are left out. The approve/reject breakdown
  // — field key + the raw extracted value — is reported so callers can log it.
  const handleConfirm = () => {
    const approvedKeys = allFields
      .filter((f) => status[f.key] === "approved")
      .map((f) => f.key);
    const toDecision = (f) => ({
      field: f.key,
      value: ingestionFields?.[f.key]?.value ?? null,
    });
    const decisions = {
      approved: allFields
        .filter((f) => status[f.key] === "approved")
        .map(toDecision),
      rejected: allFields
        .filter((f) => status[f.key] === "rejected")
        .map(toDecision),
    };
    // Third arg is append-only: callers that ignore it (the upload drawer) are
    // unaffected, and it is always empty unless `editable` is set.
    const overrides = {};
    approvedKeys.forEach((k) => {
      if (coerced[k] != null && coerced[k] !== EDIT_INVALID) {
        overrides[k] = coerced[k];
      }
    });
    // Fourth arg is append-only, like the third: both versions of every searched field,
    // for callers that persist provenance.
    // Fifth arg is append-only like the third and fourth: what the research run learned
    // about the company, for callers that keep provenance.
    onConfirm(
      approvedKeys,
      decisions,
      overrides,
      sourceRecords,
      webSummary ? { company_summary: webSummary } : null
    );
  };

  const denom = allFields.length;
  const done = allFields.filter((f) => status[f.key] === "approved").length;
  const pct = denom ? Math.round((done / denom) * 100) : 0;
  const selCount = Object.keys(selected).length;
  const carryCount = done;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 1120,
            maxWidth: "calc(100vw - 48px)",
            borderRadius: "18px",
            overflow: "hidden",
          },
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          px: "26px",
          py: "18px",
          borderBottom: `1px solid ${MUT(0.1)}`,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: GREEN,
            }}
          >
            {"Submission Review"}
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              color: INK,
              letterSpacing: "-0.01em",
            }}
            noWrap
          >
            {title || "New submission"}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 170,
            height: 7,
            bgcolor: MUT(0.08),
            borderRadius: "9999px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${pct}%`,
              height: "100%",
              bgcolor: GREEN,
              borderRadius: "9999px",
              transition: "width .35s cubic-bezier(.22,1,.36,1)",
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${done}/${denom} verified`}
        </Typography>

        {invalidKeys.length > 0 && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: MAG }}>
            {`${invalidKeys.length} edited value${
              invalidKeys.length > 1 ? "s" : ""
            } can't be read — fix or revert to continue`}
          </Typography>
        )}

        {searchError && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: MAG }}>
            {`Agent search failed — ${searchError}`}
          </Typography>
        )}

        {webChecks && webMismatchCount > 0 && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: MAG }}>
            {`Web differs on ${webMismatchCount} field${
              webMismatchCount > 1 ? "s" : ""
            }`}
          </Typography>
        )}

        {onAgentSearch && agentSearchConfig?.enabled && (
          <Pill
            variant={"outline"}
            disabled={webLoading || searchFieldKeys.length === 0}
            onClick={runAgentSearch}
            // Fits the longest label, so the header does not reflow as the count changes
            // or the button drops to "Searching…".
            sx={{
              width: 186,
              fontVariantNumeric: "tabular-nums",
              // Disabled while it runs, but the spinner is the point — keep it crisp.
              ...(webLoading && {
                opacity: 1,
                color: DEEP,
                borderColor: "rgba(44,171,99,.45)",
              }),
            }}
          >
            {webLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "7px",
                }}
              >
                <DefaultLoader type={"TailSpin"} color={GREEN} size={14} />
                {"Searching…"}
              </Box>
            ) : (
              `Run web verification (${searchFieldKeys.length})`
            )}
          </Pill>
        )}

        <Pill
          variant={"soft"}
          disabled={selCount === 0}
          onClick={approveSelected}
        >
          {`Approve selected (${selCount})`}
        </Pill>
        <Pill
          variant={"primary"}
          sx={{ px: "18px" }}
          disabled={carryCount === 0 || invalidKeys.length > 0}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Pill>
        <IconButton
          size={"small"}
          onClick={onClose}
          sx={{ color: MUT(0.5), ml: "-4px" }}
        >
          <CloseIcon fontSize={"small"} />
        </IconButton>
      </Box>

      {/* ── Grid ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          maxHeight: "62vh",
          overflow: "auto",
          bgcolor: "#fbfbfa",
          scrollbarWidth: "thin",
          scrollbarColor: `${MUT(0.2)} transparent`,
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": {
            background: MUT(0.18),
            borderRadius: "9999px",
          },
        }}
      >
        {allFields.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, color: MUT(0.6) }}>
              {"No AI-extracted fields are available to review."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Column head */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                px: "26px",
                py: "10px",
                position: "sticky",
                top: 0,
                zIndex: 2,
                bgcolor: "#f0efea",
                borderBottom: `1px solid ${MUT(0.12)}`,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: MUT(0.5),
              }}
            >
              <Box sx={COLS.check} />
              {showVerify && <Box sx={COLS.web} />}
              <Box sx={COLS.field}>{"Field"}</Box>
              <Box sx={COLS.value}>{"Value"}</Box>
              <Box sx={COLS.source}>{"Source"}</Box>
              {showConfidence && <Box sx={COLS.conf}>{"Confidence"}</Box>}
              <Box sx={{ ...COLS.status, textAlign: "right" }}>{"Status"}</Box>
            </Box>

            {grouped.map((group) => {
              const allSel =
                group.fields.length > 0 &&
                group.fields.every((f) => selected[f.key]);
              const someSel = group.fields.some((f) => selected[f.key]);
              return (
                <Box key={group.name}>
                  {/* Section head */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      px: "26px",
                      py: "9px",
                      bgcolor: "#eef4ef",
                      borderBottom: `1px solid ${MUT(0.08)}`,
                    }}
                  >
                    <CheckBox
                      checked={allSel}
                      indeterminate={!allSel && someSel}
                      onClick={() => toggleSection(group.name)}
                      label={`Select all in ${group.name}`}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: DEEP,
                      }}
                    >
                      {group.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, fontWeight: 600, color: MUT(0.45) }}
                    >
                      {`${group.fields.length} field${
                        group.fields.length !== 1 ? "s" : ""
                      }`}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Pill
                      variant={"section"}
                      sx={{ px: "12px", py: "5px", fontSize: 11.5 }}
                      onClick={() => approveSection(group.name)}
                    >
                      {"Approve section"}
                    </Pill>
                  </Box>

                  {group.fields.map((field) => (
                    <FieldRow
                      key={field.key}
                      field={field}
                      status={status[field.key]}
                      selected={selected[field.key]}
                      onToggle={toggle}
                      onApprove={approve}
                      onReject={reject}
                      onReset={reset}
                      onDownloadSource={onDownloadSource}
                      showConfidence={showConfidence}
                      editable={editable}
                      draft={drafts[field.key]}
                      invalid={coerced[field.key] === EDIT_INVALID}
                      onDraft={setDraft}
                      onRevert={revertDraft}
                      showVerify={showVerify}
                      verifyMarked={!!verifyPicks[field.key]}
                      onToggleVerify={toggleVerify}
                      webCheck={webChecks?.[field.key] || null}
                      sourceRecord={sourceRecords[field.key] || null}
                    />
                  ))}
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {/* ── Footer note ────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: "26px",
          py: "12px",
          borderTop: `1px solid ${MUT(0.1)}`,
          bgcolor: "#fff",
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: MUT(0.5) }}>
          {`Only approved fields are carried into the submission — ${carryCount} of ${denom} approved so far.`}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SubmissionReviewDialog;
